'use client';

import { useActivitiesByProfessionalId } from '@website/hooks/useActivitiesByProfessional';
import { useCurrentProfessional } from '@website/hooks/useCurrentProfessional';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { useInstallNudge } from '@website/hooks/useInstallNudge';
import { useStripeAccountStatus } from '@website/hooks/useStripeAccountStatus';
import { hasUpcomingOnlinePaymentSlot } from '@website/utils/slotPaymentCheck';

/**
 * Identifier for an active call-to-action across the user's surfaces
 * (Settings menu rows, mentor Dashboard "Needs your attention" card,
 * top-level navigation badges).
 *
 * The IDs are split into two namespaces:
 *
 * 1) `settings-*` — every CTA that materialises as a visible marker
 *    (badge pill, dot, dedicated row) somewhere inside the Settings menu.
 *    These drive the Settings tab badge in the bottom nav and the desktop
 *    Settings icon dot in the header. The invariant is:
 *      `settings count === number of visible markers in the Settings menu`
 *    so any non-zero badge always points to something the user can find.
 *
 * 2) `dashboard-*` — richer CTAs that drive the mentor Dashboard's
 *    "Needs your attention" card. They can overlap conceptually with the
 *    settings CTAs but render with a different vocabulary (severity, copy,
 *    deep links to specific edit screens) so we keep them as their own
 *    IDs to avoid coupling rendering decisions to settings-menu logic.
 *
 * Order in the union is the canonical display order used by
 * `useUserCTAs().list` (settings CTAs first, then dashboard CTAs).
 */
export type UserCTAId =
  // Settings menu CTAs --------------------------------------------------
  | 'settings-profile-completion' // Mentor "Complete your profile" entry surfaces at the top of the menu.
  | 'settings-profile-verification' // Profile row pill: "To complete" / "Under review" / "Action required".
  | 'settings-payments-action' // Payments row pill when Stripe needs onboarding or attention.
  | 'settings-install-app' // "Install Application" row dot (mobile non-standalone, eligible users only).
  // Dashboard "Needs your attention" CTAs -------------------------------
  | 'dashboard-stripe-onboarding' // Stripe onboarding incomplete + at least one online-payment slot exists.
  | 'dashboard-stripe-action' // Existing Stripe account needs additional info / verification.
  | 'dashboard-pro-revision-declined' // Latest professional-profile revision was declined by an admin.
  | 'dashboard-activity-revision-declined' // An activity revision was declined (rejection includes 'activities').
  | 'dashboard-profile-incomplete'; // Mentor still missing required profile fields (name / phone / photo) AND not yet approved.

/**
 * Filter helper: every settings CTA has a stable `settings-` prefix and
 * every dashboard CTA has a stable `dashboard-` prefix. Used by `count`
 * sub-totals below; kept exported for call sites that want to slice the
 * full list themselves.
 */
export type SettingsCTAId = Extract<UserCTAId, `settings-${string}`>;
export type DashboardCTAId = Extract<UserCTAId, `dashboard-${string}`>;

export interface UserCTAState {
  /** All active CTA IDs, ordered to match canonical display order. */
  list: UserCTAId[];
  /** Sub-list of active settings CTAs (drives Settings menu markers + bottom-nav badge). */
  settingsList: SettingsCTAId[];
  /** Sub-list of active dashboard CTAs (drives Dashboard "Needs your attention"). */
  dashboardList: DashboardCTAId[];
  /** Total count across all surfaces. Rarely useful — most call sites want one of the sub-counts. */
  count: number;
  /** Settings sub-count — the source of truth for the Settings tab badge. */
  settingsCount: number;
  /** Dashboard sub-count — the source of truth for the dashboard CTA card header. */
  dashboardCount: number;
  /** Convenience predicate equivalent to `list.includes(id)`. */
  has: (id: UserCTAId) => boolean;
}

/**
 * Single source of truth for "what should attract the user's attention
 * right now?" across every surface that renders a CTA.
 *
 * Composes the underlying data hooks (`useStripeAccountStatus`,
 * `useInstallNudge`, `useCurrentProfessional`, `useActivitiesByProfessionalId`)
 * so every consumer — bottom-nav badge, mobile settings rows, desktop
 * sidebar pills, header dot, dashboard attention list — agrees on which
 * markers to render and how to count them.
 *
 * Adding a new CTA in the future = adding a new branch here and a new
 * value to `UserCTAId`. Settings call sites pick it up automatically via
 * `settingsCount`; dashboard call sites opt in by id via `has(...)`.
 */
export function useUserCTAs(): UserCTAState {
  const { data: user } = useCurrentUser();
  const { professional } = useCurrentProfessional();
  const { showNudgeBadge: installNudgeActive } = useInstallNudge();

  const isProfessional = user?.isProfessional === true;
  const proId = isProfessional ? user?.id : undefined;

  // Stripe + activities feed both the settings "payments-action" pill and
  // the dashboard's two distinct stripe items. Reading them here once so
  // every consumer hits the same React-Query cache. Both queries are
  // already mounted by `usePaymentsDashboard`/`useStripeActionRequired`
  // call sites elsewhere; if neither is mounted they cost a single fetch
  // each, gated on `proId`.
  const { data: stripeStatus } = useStripeAccountStatus(proId);
  const { data: activitiesData } = useActivitiesByProfessionalId(proId, '', undefined);
  // Only *upcoming* online-payment slots count — a purely-past online slot can
  // no longer take an online booking, so it must not drive any onboarding nudge.
  const hasOnlinePaymentSlots = hasUpcomingOnlinePaymentSlot(activitiesData);

  // ---- Stripe-derived predicates ------------------------------------------------
  // Onboarding is only worth nudging once the mentor has a slot that
  // accepts online payments. Until then there's literally nothing to unlock
  // by completing onboarding — any nudge would be noise.
  const stripeOnboardingNeeded = !!stripeStatus && !stripeStatus.onboardingComplete && hasOnlinePaymentSlots;

  // Post-onboarding "requires action" only counts if the account exists —
  // the backend defaults `requiresAction: true` for accounts that haven't
  // been created yet (see `getStripeAccountStatus.ts`), and acting on that
  // pre-creation surfaces a phantom CTA on every professional's surfaces
  // before they've ever opened the payments page.
  const stripeActionNeeded = !!stripeStatus && stripeStatus.hasStripeAccount && stripeStatus.requiresAction;

  // For the *settings* surfaces we collapse the two cases into a single
  // "payments-action" marker (the menu only renders one pill), but the
  // dashboard cares about the distinction (different copy, different
  // severity). Keep both shapes available.
  const settingsPaymentsAction = stripeOnboardingNeeded || stripeActionNeeded;

  // ---- Mentor profile predicates ------------------------------------------------
  const hasPendingRevision = professional?.hasPendingRevision ?? false;
  const isUnderReview = hasPendingRevision || professional?.status === 'pending';
  const isIncomplete = professional?.status === 'incomplete' || professional?.status === 'rejected';
  const profileVerificationMarker = isProfessional && (isUnderReview || isIncomplete);

  // "Actionable not-approved" — mentor can actually do something about it.
  // Excludes `pending` / `hasPendingRevision` because while a review is in
  // flight the profile is read-only; nagging the user to "complete" it then
  // is misleading and inflates the settings counter with no clickable target.
  // Only `incomplete` / `rejected` (and not currently under review) qualify.
  const profileNeedsCompletion = isProfessional && !!professional && isIncomplete && !isUnderReview;

  // Latest revision was reviewed and declined — the mentor needs to edit
  // and resubmit. We separate this from the generic "pro_revision" case
  // because the dashboard surfaces it with edit/resubmit copy specifically.
  const proRevisionDeclined = professional?.latestRevision?.status === 'declined';

  // The activities-section rejection is independent of the profile-level
  // revision: an admin can reject only the activities section while the
  // overall revision is still pending or approved. We surface it whenever
  // 'activities' is in `rejectionSections` AND we haven't already shown
  // the broader profile-revision-declined item (de-dup).
  const activityRevisionDeclined = !!professional?.rejectionSections?.includes('activities') && !proRevisionDeclined;

  // Backend-owned: `status === 'incomplete'` IS the "required fields not all
  // filled in" signal — `reevaluateProfessionalStatus` flips it to `pending`
  // the moment `isProfessionalProfileComplete` (first/last name, mobile, photo,
  // ID document, at least one bio locale) passes. Re-checking a subset of
  // those fields client-side would drift from the server, so we just reuse
  // `profileNeedsCompletion` (which is `incomplete|rejected && !underReview`).
  const dashboardProfileIncomplete = profileNeedsCompletion;

  // ---- Build the ordered list ---------------------------------------------------
  const list: UserCTAId[] = [];

  // Settings CTAs first — preserves the order used by every settings call
  // site that iterates `settingsList` directly.
  if (profileNeedsCompletion) list.push('settings-profile-completion');
  if (profileVerificationMarker) list.push('settings-profile-verification');
  if (settingsPaymentsAction) list.push('settings-payments-action');
  if (installNudgeActive) list.push('settings-install-app');

  // Dashboard CTAs in the visual order that `NeedsAttention.tsx` renders:
  // critical (Stripe) first, then warnings (revisions), then info
  // (profile incomplete).
  if (stripeOnboardingNeeded) list.push('dashboard-stripe-onboarding');
  // The dashboard renders only one Stripe item even when both predicates
  // are true (onboarding incomplete naturally implies action). Mirror that
  // mutual exclusion here so `count` doesn't double-bill.
  else if (stripeActionNeeded) list.push('dashboard-stripe-action');
  if (proRevisionDeclined) list.push('dashboard-pro-revision-declined');
  if (activityRevisionDeclined) list.push('dashboard-activity-revision-declined');
  if (dashboardProfileIncomplete) list.push('dashboard-profile-incomplete');

  const settingsList = list.filter((id): id is SettingsCTAId => id.startsWith('settings-'));
  const dashboardList = list.filter((id): id is DashboardCTAId => id.startsWith('dashboard-'));

  return {
    list,
    settingsList,
    dashboardList,
    count: list.length,
    settingsCount: settingsList.length,
    dashboardCount: dashboardList.length,
    has: (id) => list.includes(id)
  };
}
